import { useEffect, useRef } from 'react';


/**
 * @description Hook that runs a callback when the tab is refocused. The component must be mounted,
 * otherwise the hook will not run.
 *
 * The callback is held in a ref so the `visibilitychange` listener is registered **once per mount**.
 * Depending on `[callback]` re-subscribed on every render, because callers pass inline arrows whose
 * identity changes each time — churn that bought nothing.
 *
 * @param callback - your callback function
 *
 * @example
 * const myCallback = () => {
 *  console.log('Tab refocused!');
 * };
 *
 * useOnTabRefocus(myCallback);
 */
export const useOnTabRefocus = (callback: () => void) => {
  // Always invoke the latest callback without re-registering the listener.
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    // when tab is refocused, run the callback
    const tabFocusCallback = () => {
      if (document.visibilityState === 'visible') {
        callbackRef.current();
      }
    };

    // Add event listener
    document.addEventListener('visibilitychange', tabFocusCallback, false);

    // Cleanup the effect and remove the event listener
    return () => {
      document.removeEventListener('visibilitychange', tabFocusCallback, false);
    };
  }, []);
};
